import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Bouton, Etiquette } from '../components/ui/Base'
import { EtatVide, MessageErreur } from '../components/ui/Etats'
import { Squelette } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import type { Ordonnance, Page, Patient, TypeSoin } from '../lib/api/types'
import { formatDate } from '../lib/formatters'
import { decrireLigneOrdonnance } from '../lib/formatters/recurrence'

type Onglet = 'ordonnances' | 'interventions' | 'historique'

export function PatientDetail() {
  const { id = '' } = useParams()
  const naviguer = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [onglet, setOnglet] = useState<Onglet>('ordonnances')

  const requete = useQuery({
    queryKey: cles.patients.detail(id),
    queryFn: ({ signal }) => api.get<Patient>(`/patients/${id}`, signal),
    enabled: Boolean(id),
  })

  const requeteOrdonnances = useQuery({
    queryKey: cles.patients.ordonnances(id),
    queryFn: ({ signal }) => api.get<Ordonnance[]>(`/patients/${id}/ordonnances`, signal),
    enabled: Boolean(id),
  })

  const requeteSoins = useQuery({
    queryKey: cles.typesSoins.liste(),
    queryFn: ({ signal }) => api.get<Page<TypeSoin>>('/types-soins', signal),
    staleTime: 5 * 60_000,
  })

  const geocoder = useMutation({
    mutationFn: () => api.post<Patient>(`/patients/${id}/geocoder`),
    onSuccess: () => {
      toast('Adresse géolocalisée.', 'succes')
      void queryClient.invalidateQueries({ queryKey: cles.patients.detail(id) })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Géocodage impossible.', 'erreur'),
  })

  if (requete.isPending) return <Squelette lignes={8} />
  if (requete.isError) return <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />

  const patient = requete.data

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/patients" className="text-sm text-sky-700 hover:underline">
            ← Patients
          </Link>
          <h1 className="mt-1 text-xl font-semibold text-slate-900">
            {patient.nom} {patient.prenom}
          </h1>
          <p className="text-sm text-slate-600">
            {patient.adresse}
            {patient.complement_adresse ? `, ${patient.complement_adresse}` : ''} —{' '}
            {patient.code_postal} {patient.ville}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!patient.actif ? <Etiquette>Inactif</Etiquette> : null}
          <Link
            to={`/patients/${patient.id}/modifier`}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            Modifier la fiche
          </Link>
          {patient.geocodage_statut !== 'OK' ? (
            <>
              <Etiquette ton="attention">Non géolocalisé</Etiquette>
              <Bouton
                variante="secondaire"
                onClick={() => geocoder.mutate()}
                disabled={geocoder.isPending}
              >
                {geocoder.isPending ? 'Géocodage…' : 'Géocoder'}
              </Bouton>
            </>
          ) : null}
        </div>
      </div>

      {/* ==================================================================
          L'ENCART D'ACCÈS EST EN HAUT DE PAGE, AU-DESSUS DU PLI.

          Jamais dans un onglet. Le §5.5 est formel : ces champs ne sont pas du
          décor. Un digicode qu'il faut aller chercher dans un troisième onglet
          coûte dix minutes par passage, et c'est exactement ce que le logiciel
          est censé faire gagner.
          ================================================================== */}
      <section
        data-testid="encart-acces"
        aria-labelledby="titre-acces"
        className="rounded-lg border-2 border-slate-300 bg-white p-4"
      >
        <h2 id="titre-acces" className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          Accès au domicile
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs text-slate-500">Digicode</p>
            <p className="font-mono text-2xl font-bold tracking-wider text-slate-900">
              {patient.digicode ?? <span className="text-base font-normal text-slate-400">Non renseigné</span>}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Interphone</p>
            <p className="text-lg font-medium text-slate-900">
              {patient.interphone ?? <span className="text-base font-normal text-slate-400">Non renseigné</span>}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Étage</p>
            <p className="text-lg font-medium text-slate-900">
              {patient.etage ?? <span className="text-base font-normal text-slate-400">Non renseigné</span>}
              {patient.ascenseur === true ? (
                <span className="ml-2 text-sm font-normal text-slate-600">avec ascenseur</span>
              ) : patient.ascenseur === false ? (
                <span className="ml-2 text-sm font-normal text-slate-600">sans ascenseur</span>
              ) : null}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Notes d&apos;accès</p>
            <p className="text-sm text-slate-800">
              {patient.acces_notes ?? <span className="text-slate-400">Non renseigné</span>}
            </p>
          </div>
        </div>

        {patient.presence_animaux ? (
          <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
            ⚠ Animaux : {patient.presence_animaux}
          </p>
        ) : null}
      </section>

      {/* ── Identité et médical ── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Identité</h2>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Ligne libelle="Date de naissance" valeur={formatDate(patient.date_naissance)} />
            <Ligne libelle="Sexe" valeur={patient.sexe ?? '—'} />
            <Ligne libelle="Téléphone" valeur={patient.telephone ?? '—'} />
            <Ligne libelle="Téléphone secondaire" valeur={patient.telephone_secondaire ?? '—'} />
            <Ligne libelle="Courriel" valeur={patient.email ?? '—'} />
            {/* Masqué hors TITULAIRE (R6) : on l'écrit, jamais une chaîne vide
                silencieuse qui laisserait croire à une donnée manquante. */}
            <Ligne
              libelle="N° de sécurité sociale"
              valeur={patient.numero_secu ?? 'Masqué'}
              discret={patient.numero_secu === null}
            />
          </dl>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Médical et contacts
          </h2>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Ligne libelle="Médecin traitant" valeur={patient.medecin_traitant ?? '—'} />
            <Ligne libelle="Téléphone médecin" valeur={patient.medecin_traitant_telephone ?? '—'} />
            <Ligne
              libelle="Contact d'urgence"
              valeur={
                patient.contact_urgence_nom
                  ? `${patient.contact_urgence_nom} (${patient.contact_urgence_lien ?? '—'}) ${patient.contact_urgence_telephone ?? ''}`
                  : '—'
              }
            />
            <Ligne libelle="Personne de confiance" valeur={patient.personne_de_confiance ?? '—'} />
            <Ligne libelle="Allergies" valeur={patient.allergies ?? '—'} />
            <Ligne libelle="Antécédents" valeur={patient.antecedents ?? '—'} />
            <Ligne libelle="Traitements en cours" valeur={patient.traitements_en_cours ?? '—'} />
          </dl>
        </section>
      </div>

      {/* ── Onglets ── */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div role="tablist" aria-label="Informations du patient" className="flex border-b border-slate-200">
          {(
            [
              ['ordonnances', 'Ordonnances'],
              ['interventions', 'Interventions à venir'],
              ['historique', 'Historique'],
            ] as const
          ).map(([cle, libelle]) => (
            <button
              key={cle}
              role="tab"
              aria-selected={onglet === cle}
              onClick={() => setOnglet(cle)}
              className={`px-4 py-2.5 text-sm font-medium ${
                onglet === cle
                  ? 'border-b-2 border-sky-600 text-sky-700'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {libelle}
            </button>
          ))}
        </div>

        <div className="p-4">
          {onglet === 'ordonnances' ? (
            requeteOrdonnances.isPending ? (
              <Squelette lignes={4} />
            ) : (requeteOrdonnances.data?.length ?? 0) === 0 ? (
              <EtatVide
                icone="📄"
                titre="Aucune ordonnance"
                description="Sans ordonnance, aucune intervention ne sera générée pour ce patient."
                action={
                  <Bouton onClick={() => naviguer(`/patients/${patient.id}/ordonnances/nouvelle`)}>
                    Saisir une ordonnance
                  </Bouton>
                }
              />
            ) : (
              <ul className="space-y-3">
                <li className="flex justify-end">
                  <Bouton
                    variante="secondaire"
                    onClick={() => naviguer(`/patients/${patient.id}/ordonnances/nouvelle`)}
                  >
                    Ajouter une ordonnance
                  </Bouton>
                </li>
                {requeteOrdonnances.data?.map((ordonnance) => (
                  <li key={ordonnance.id} className="rounded-lg border border-slate-200 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-800">
                        {ordonnance.medecin_prescripteur} — prescrite le{' '}
                        {formatDate(ordonnance.date_prescription)}
                      </p>
                      <Etiquette ton={ordonnance.statut === 'ACTIVE' ? 'succes' : 'neutre'}>
                        {ordonnance.statut}
                      </Etiquette>
                    </div>
                    <ul className="mt-2 space-y-1 text-sm text-slate-700">
                      {(ordonnance.lignes ?? []).map((ligne) => {
                        const soin = requeteSoins.data?.items.find((t) => t.id === ligne.type_soin_id)
                        return (
                          <li key={ligne.id} className="flex items-start gap-2">
                            <span
                              className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                              style={{ backgroundColor: soin?.couleur ?? '#94a3b8' }}
                              aria-hidden="true"
                            />
                            {/* La phrase, pas les données brutes. */}
                            {decrireLigneOrdonnance(
                              ligne,
                              soin?.libelle ?? 'Soin',
                              ordonnance.date_debut,
                              ordonnance.date_fin,
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <EtatVide
              icone="🗓️"
              titre="À venir"
              description="Cet onglet sera alimenté par le planning une fois le lot C fusionné."
            />
          )}
        </div>
      </div>
    </div>
  )
}

function Ligne({ libelle, valeur, discret }: { libelle: string; valeur: string; discret?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{libelle}</dt>
      <dd className={discret ? 'italic text-slate-400' : 'text-slate-900'}>{valeur}</dd>
    </div>
  )
}
